import { Component, ChangeDetectionStrategy, input, output, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { SwitchModule } from '@progress/kendo-angular-inputs';
import { MvssFormService, MvssFormData } from './services/form.service';

@Component({
  selector: 'app-mvss-service',
  standalone: true,
  templateUrl: './mvss-service.component.html',
  styleUrls: ['./mvss-service.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MvssFormService],
  imports: [CommonModule, ReactiveFormsModule, InputsModule, LabelModule, ButtonsModule, IconsModule, SwitchModule],
})
export class MvssServiceComponent {
  private formService = inject(MvssFormService);

  // Input/Output - simplified
  public initialData = input<MvssFormData | null>(null); // Optional initial data for editing
  public isNarrowLayout = input<boolean>(false);
  public dataUpdate = output<MvssFormData>();
  public validityUpdate = output<boolean>();

  // Form - self-managed
  public mvssForm = this.formService.createMvssForm();

  // Service data
  public vssServiceOptions = this.formService.getServiceOptions();

  constructor() {
    this.initializeForm();
  }

  private initializeForm(): void {
    // Initialize with existing data if available
    const initialData = this.initialData();
    if (initialData) {
      this.mvssForm = this.formService.createMvssForm(initialData);
    }

    // Watch for form changes and emit updates
    this.mvssForm.valueChanges.subscribe((value) => {
      const formData = this.formService.getFormData(this.mvssForm);
      this.dataUpdate.emit(formData);
      this.validityUpdate.emit(this.mvssForm.valid);
    });

    // Emit initial validity state
    this.validityUpdate.emit(this.mvssForm.valid);
  }

  public isServiceSelected(serviceValue: string): boolean {
    return this.mvssForm.get('selectedService')?.value === serviceValue;
  }

  public selectService(serviceValue: string): void {
    this.formService.selectService(this.mvssForm, serviceValue);
  }

  public getServiceDescription(serviceValue: string): string {
    return this.formService.getServiceDescription(serviceValue);
  }

  public onTotalMachinesChange(value: number | null): void {
    this.formService.onTotalMachinesChange(this.mvssForm, value);
  }

  // Debug method - call this from browser console to check form state
  public debugFormState(): void {
    this.formService.debugFormState(this.mvssForm);
  }
}
